/**
 * Política de acesso do plano freemium — fonte única de verdade.
 *
 * Regras:
 * - Dentro de `/disciplinas`: o freemium pode interagir, mas só consome
 *   conteúdo liberado (com a tag `freemium: true`). O resto é bloqueado com a
 *   mensagem/modal de upsell.
 * - Fora de `/disciplinas`: o freemium é somente-leitura (pode visualizar, não
 *   pode disparar ações mutativas).
 *
 * Mantenha estas funções puras (sem hooks/stores) para poderem ser usadas tanto
 * em componentes (via useAccess) quanto no interceptor do axios.
 */

/** Rotas onde o freemium pode interagir/mutar (sujeito à tag do conteúdo). */
export function canInteractOnRoute(pathname: string): boolean {
  return pathname.startsWith('/disciplinas');
}

/** Conteúdo de disciplina liberado para o freemium (possui a tag). */
export function isFreemiumContent(item?: { freemium?: boolean }): boolean {
  return !!item?.freemium;
}

/**
 * Endpoints mutativos (POST/PUT/PATCH/DELETE) que o freemium PODE disparar
 * mesmo no modo somente-leitura. Cobre auth, marcar-visualizado/versão,
 * checkout/assinatura e perfil/configurações.
 *
 * Tracking/GA (via fetch) e badge (gameAxiosInstance) não passam pelo
 * interceptor do axiosInstance principal, por isso não precisam estar aqui.
 */
const WRITE_WHITELIST: RegExp[] = [
  // Auth
  /\/auth\/login$/,
  /\/auth\/me$/,
  /\/password\/forgout$/,
  /\/password$/,
  /\/user\/create-pwd$/,
  // Marcar visualizado / versão da plataforma
  /\/lessons\/[^/]+\/finished$/,
  /\/workbooks\/[^/]+\/opened$/,
  /\/mindmaps\/[^/]+\/opened$/,
  /\/podcasts\/[^/]+\/opened$/,
  /\/exercises\/[^/]+\/finish$/,
  /\/mobile\/courses\/[^/]+\/content\/[^/]+\/exercises\/[^/]+\/finish$/,
  /\/content-views$/,
  /\/user\/version$/,
  // Checkout / assinatura / matrícula
  /\/checkout\/[^/]+\/address$/,
  /\/simuladaoaprova\//,
  // Perfil / configurações
  /\/user\/profilePlatform$/,
  /\/user\/editpassword$/,
  /\/leveling\/profile$/,
  /\/user\/profile$/,
  /\/user\/reset$/,
  /\/user\/upload\/picture$/,
];

/** A URL corresponde a um endpoint mutativo liberado para o freemium? */
export function isWriteWhitelisted(url?: string): boolean {
  if (!url) return false;
  const path = url.split('?')[0];
  return WRITE_WHITELIST.some((pattern) => pattern.test(path));
}

/**
 * Decide, no momento da request, se o freemium está em modo somente-leitura.
 * Puro em relação a React (lê a store e o pathname global) para uso no
 * interceptor do axios.
 */
export function isFreemiumReadOnly(
  isFreemium: boolean,
  pathname: string
): boolean {
  return isFreemium && !canInteractOnRoute(pathname);
}
